/**
 * Offer Price Storage Utilities
 *
 * Provides localStorage utilities for storing and retrieving Player API prices
 * to detect price deviations between Offer page and Checkout page.
 *
 * Data Flow:
 * 1. OfferPage saves prices before redirect to checkout
 * 2. CheckoutPage loads and validates prices after availability check
 * 3. Data is NOT deleted on checkout load (preserved for page refresh)
 * 4. Data is overwritten when new offer is selected
 */

import type { RoomCombination } from '@/services/mappers/offerMapper';
import * as Sentry from '@sentry/vue';

// ============================================================================
// Constants
// ============================================================================

export const OFFER_PRICES_STORAGE_KEY = 'ibe_offer_prices';
export const OFFER_PRICES_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// ============================================================================
// Types
// ============================================================================

export interface OfferIdentifier {
  productCode: string;
  offerIds: string;
  roomCodes: string;
  boardCode: string;
  checkIn: string;
  checkOut: string;
  pax: string;
}

export interface StoredOfferPrices {
  identifier: OfferIdentifier;
  totalPrice: number;
  savedAt: string;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Create identifier from RoomCombination for storage
 *
 * @param combination - The room combination from offer page
 * @param productCode - Product code (GIATA ID)
 * @param paxString - Serialized PAX string
 * @returns OfferIdentifier for matching
 */
export function createIdentifierFromCombination(
  combination: RoomCombination,
  productCode: string,
  paxString: string
): OfferIdentifier {
  return {
    productCode,
    offerIds: combination.products.map((p) => p.offerId).join('-'),
    roomCodes: combination.products.map((p) => p.roomCode).join('-'),
    boardCode: combination.boardCode,
    // Convert from ISO string to YYYY-MM-DD
    checkIn: combination.startDate.split('T')[0],
    checkOut: combination.endDate.split('T')[0],
    pax: paxString,
  };
}

/**
 * Create identifier from checkout URL parameters
 *
 * @param productCode - Product code
 * @param offerIds - Offer IDs joined with dash
 * @param roomCodes - Room codes joined with dash
 * @param boardCode - Board code
 * @param checkIn - Check-in date YYYY-MM-DD
 * @param checkOut - Check-out date YYYY-MM-DD
 * @param pax - PAX string
 * @returns OfferIdentifier for matching
 */
export function createIdentifierFromCheckout(
  productCode: string,
  offerIds: string,
  roomCodes: string,
  boardCode: string,
  checkIn: string,
  checkOut: string,
  pax: string
): OfferIdentifier {
  return {
    productCode,
    offerIds,
    roomCodes,
    boardCode,
    checkIn,
    checkOut,
    pax,
  };
}

/**
 * Save offer prices to localStorage before redirect to checkout
 *
 * @param combination - The room combination being booked
 * @param productCode - Product code (GIATA ID)
 * @param paxString - Serialized PAX string
 *
 * @remarks
 * - Automatically overwrites any existing data (single key in localStorage)
 * - Silently fails if localStorage is unavailable
 */
export function saveOfferPrices(
  combination: RoomCombination,
  productCode: string,
  paxString: string
): void {
  try {
    const identifier = createIdentifierFromCombination(combination, productCode, paxString);

    const data: StoredOfferPrices = {
      identifier,
      totalPrice: combination.totalPrice,
      savedAt: new Date().toISOString(),
    };

    localStorage.setItem(OFFER_PRICES_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn('[offerPriceStorage] Failed to save offer prices:', error);
  }
}

/**
 * Load stored offer prices from localStorage
 *
 * @returns StoredOfferPrices if valid data exists, null otherwise
 *
 * @remarks
 * - Returns null if data doesn't exist, is invalid, or is expired (>7 days)
 * - Expired data is automatically cleared
 */
export function loadOfferPrices(): StoredOfferPrices | null {
  try {
    const raw = localStorage.getItem(OFFER_PRICES_STORAGE_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw) as StoredOfferPrices;

    // Validate structure
    if (!data.identifier || !data.savedAt || typeof data.totalPrice !== 'number') {
      console.warn('[offerPriceStorage] Invalid data structure');
      return null;
    }

    // Check expiration (7 days)
    const savedAt = new Date(data.savedAt).getTime();
    const now = Date.now();
    if (now - savedAt > OFFER_PRICES_MAX_AGE_MS) {
      console.warn('[offerPriceStorage] Data expired');
      clearOfferPrices();
      return null;
    }

    return data;
  } catch (error) {
    console.warn('[offerPriceStorage] Failed to load offer prices:', error);
    return null;
  }
}

/**
 * Check if stored prices match the current checkout offer
 *
 * @param stored - Stored offer prices data
 * @param currentIdentifier - Identifier from current checkout
 * @returns true if all identifier fields match
 */
export function matchesCurrentOffer(
  stored: StoredOfferPrices,
  currentIdentifier: OfferIdentifier
): boolean {
  const s = stored.identifier;
  const c = currentIdentifier;

  const matches =
    s.productCode === c.productCode &&
    s.offerIds === c.offerIds &&
    s.roomCodes === c.roomCodes &&
    s.boardCode === c.boardCode &&
    s.checkIn === c.checkIn &&
    s.checkOut === c.checkOut &&
    s.pax === c.pax;

  if (!matches) {
    Sentry.addBreadcrumb({
      category: 'offerPriceStorage',
      message: 'Identifier mismatch - stored prices do not match current offer',
      level: 'info',
      data: { stored: s, current: c },
    });
  }

  return matches;
}

/**
 * Clear stored offer prices from localStorage
 *
 * @remarks Used for clearing expired data or manual cleanup
 */
export function clearOfferPrices(): void {
  try {
    localStorage.removeItem(OFFER_PRICES_STORAGE_KEY);
  } catch (error) {
    console.warn('[offerPriceStorage] Failed to clear offer prices:', error);
  }
}
